import React, { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Client } from '@/db';
import db from '@/db/db';
import { syncContact } from '@/db/services/contact_service';
import { Contact, getContactRoles } from '@/db/types/contact';
import { decoratePerson } from '@/db/types/person';
import { Program, Site } from '@/db/types/user';
import { formatPhoneDisplay } from '@/lib/format-phone-display';
import { Button } from '@mes-amis/galette-design-system';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

interface ProfileTabProps {
  client: Client;
  clientId: number;
}

/* ── Helpers ─────────────────────────────────── */

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-4 py-2">
      <dt className="text-sm text-gray-500 w-40 shrink-0">{label}</dt>
      <dd className="text-sm text-gray-900 min-w-0">{value || <span className="text-gray-400">Not provided</span>}</dd>
    </div>
  );
}

function formatAddress(address: Client['address'] | Contact['address']) {
  if (!address) return null;
  const parts = [address.address_line1, address.address_line2, address.city, address.state, address.zip].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
}

function formatDate(date: string | undefined) {
  if (!date) return null;
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function humanize(value: string | undefined | null): string | null {
  if (!value) return null;
  return value
    .replace(/_/g, ' ')
    .replace(/^\w/, c => c.toUpperCase());
}

function formatPhone(client: Client) {
  const phones = client.person.phone_numbers;
  if (!phones || phones.length === 0) return null;
  const primary = phones.find(p => p.primary) || phones[0];
  return formatPhoneDisplay(primary.number) || null;
}

function RoleBadge({ role }: { role: string }) {
  const colors: Record<string, string> = {
    'Emergency Contact': 'bg-red-100 text-red-800',
    'Caregiver': 'bg-purple-100 text-purple-800',
    'Proxy': 'bg-blue-100 text-blue-800',
    'Legal Representative': 'bg-green-100 text-green-800',
    'Physician': 'bg-indigo-100 text-indigo-800',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[role] || 'bg-gray-100 text-gray-800'}`}>
      {role}
    </span>
  );
}

function ContactCard({ contact }: { contact: Contact }) {
  const person = contact.person ? decoratePerson(contact.person) : null;
  const roles = getContactRoles(contact);

  return (
    <div className="border border-gray-200 rounded p-3 space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-900">
          {person?.displayName || 'Unnamed Contact'}
        </span>
        {!contact.synced && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-yellow-100 text-yellow-800">
            Unsynced
          </span>
        )}
      </div>
      {roles.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {roles.map(r => <RoleBadge key={r} role={r} />)}
        </div>
      )}
      <dl className="text-xs text-gray-600 space-y-0.5 pt-1">
        {contact.relationship && (
          <div className="flex gap-2">
            <span className="text-gray-400 w-20 shrink-0">Relationship</span>
            <span>{contact.relationship}</span>
          </div>
        )}
        {person?.email && (
          <div className="flex gap-2">
            <span className="text-gray-400 w-20 shrink-0">Email</span>
            <span>{person.email}</span>
          </div>
        )}
        {person?.phone_numbers?.[0] && (
          <div className="flex gap-2">
            <span className="text-gray-400 w-20 shrink-0">Phone</span>
            <span>{formatPhoneDisplay(person.phone_numbers[0].number)}</span>
          </div>
        )}
        {formatAddress(contact.address) && (
          <div className="flex gap-2">
            <span className="text-gray-400 w-20 shrink-0">Address</span>
            <span>{formatAddress(contact.address)}</span>
          </div>
        )}
        {contact.note && (
          <div className="flex gap-2">
            <span className="text-gray-400 w-20 shrink-0">Note</span>
            <span>{contact.note}</span>
          </div>
        )}
      </dl>
    </div>
  );
}

/* ── Section wrapper ─────────────────────────── */

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="px-5 py-2">
        {children}
      </div>
    </div>
  );
}

/* ── Main Component ──────────────────────────── */

const ProfileTab: React.FC<ProfileTabProps> = ({ client, clientId }) => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncSuccess, setSyncSuccess] = useState(false);
  const [contactsExpanded, setContactsExpanded] = useState(false);

  // Query contacts for this client
  const contacts = useLiveQuery(
    () => db.contacts.where('customer_id').equals(clientId).toArray(),
    [clientId]
  );

  // Query program and site names
  const programs = useLiveQuery(async () => {
    if (!client.program_ids || client.program_ids.length === 0) return [];
    return db.programs.where('id').anyOf(client.program_ids).toArray() as Promise<Program[]>;
  }, [client.program_ids]);

  const sites = useLiveQuery(async () => {
    if (!client.site_ids || client.site_ids.length === 0) return [];
    return db.sites.where('id').anyOf(client.site_ids).toArray() as Promise<Site[]>;
  }, [client.site_ids]);

  // Handle syncing unsynced contacts
  const handleSyncContacts = async () => {
    if (!contacts || !navigator.onLine) {
      setSyncError('Cannot sync while offline');
      return;
    }

    const unsyncedContacts = contacts.filter(c => !c.synced);
    if (unsyncedContacts.length === 0) return;

    try {
      setIsSyncing(true);
      setSyncError(null);
      setSyncSuccess(false);

      for (const contact of unsyncedContacts) {
        await syncContact(contact);
      }

      setSyncSuccess(true);
      setTimeout(() => setSyncSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to sync contacts:', err);
      setSyncError(err instanceof Error ? err.message : 'Failed to sync contacts');
    } finally {
      setIsSyncing(false);
    }
  };

  const unsyncedCount = contacts?.filter(c => !c.synced).length ?? 0;
  const person = client.person;

  return (
    <div className="space-y-5">
      {/* Personal Information */}
      <Section title="Personal Information">
        <dl className="divide-y divide-gray-100">
          <InfoRow label="Full Name" value={`${person.first_name || ''} ${person.middle_name || ''} ${person.last_name || ''}`.replace(/\s+/g, ' ').trim()} />
          {person.preferred_name && <InfoRow label="Preferred Name" value={person.preferred_name} />}
          <InfoRow label="Date of Birth" value={formatDate(person.date_of_birth)} />
          <InfoRow label="Gender" value={humanize(person.gender)} />
          <InfoRow label="Email" value={person.email} />
          <InfoRow label="Phone" value={formatPhone(client)} />
          {person.phone_numbers && person.phone_numbers.length > 1 && (
            <InfoRow
              label="Other Phones"
              value={person.phone_numbers.slice(1).map((p, i) => (
                <span key={i} className="mr-3">{formatPhoneDisplay(p.number)}{p.label ? ` (${p.label})` : ''}</span>
              ))}
            />
          )}
          <InfoRow label="Address" value={formatAddress(client.address)} />
          {client.address?.county && <InfoRow label="County" value={client.address.county} />}
        </dl>
      </Section>

      {/* Languages */}
      {(person.primary_language || (person.secondary_languages && person.secondary_languages.length > 0)) && (
        <Section title="Languages">
          <dl className="divide-y divide-gray-100">
            <InfoRow label="Primary Language" value={humanize(person.primary_language)} />
            {person.secondary_languages && person.secondary_languages.length > 0 && (
              <InfoRow label="Other Languages" value={person.secondary_languages.map(l => humanize(l)).join(', ')} />
            )}
          </dl>
        </Section>
      )}

      {/* Programs & Sites */}
      {((programs && programs.length > 0) || (sites && sites.length > 0)) && (
        <Section title="Programs & Sites">
          <dl className="divide-y divide-gray-100">
            {programs && programs.length > 0 && (
              <InfoRow
                label="Programs"
                value={
                  <div className="flex flex-wrap gap-1">
                    {programs.map(p => (
                      <span key={p.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-deep-sea-green/10 text-deep-sea-green">
                        {p.name}
                      </span>
                    ))}
                  </div>
                }
              />
            )}
            {sites && sites.length > 0 && (
              <InfoRow
                label="Sites"
                value={
                  <div className="flex flex-wrap gap-1">
                    {sites.map(s => (
                      <span key={s.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                        {s.name}
                      </span>
                    ))}
                  </div>
                }
              />
            )}
          </dl>
        </Section>
      )}

      {/* Contacts */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => setContactsExpanded(prev => !prev)}
          className="w-full flex items-center justify-between px-5 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors"
        >
          <h3 className="text-sm font-semibold text-gray-900">
            Contacts
            {contacts && contacts.length > 0 && (
              <span className="ml-2 text-xs font-normal text-gray-500">
                ({contacts.length})
              </span>
            )}
          </h3>
          <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${contactsExpanded ? 'rotate-180' : ''}`} />
        </button>

        {contactsExpanded && (
          <div className="px-5 py-3">
            {contacts === undefined ? (
              <p className="text-sm text-gray-400">Loading...</p>
            ) : contacts.length === 0 ? (
              <p className="text-sm text-gray-400">No contacts</p>
            ) : (
              <div className="space-y-3">
                {/* Sync bar */}
                {unsyncedCount > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                      {unsyncedCount} unsynced
                    </span>
                    <Button variant="primary" size="sm" onClick={handleSyncContacts} disabled={isSyncing || !navigator.onLine}>
                      {isSyncing ? 'Syncing...' : 'Sync'}
                    </Button>
                  </div>
                )}
                {syncSuccess && (
                  <div className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded">
                    Contacts synced successfully!
                  </div>
                )}
                {syncError && (
                  <div className="text-xs text-red-700 bg-red-50 px-2 py-1 rounded">
                    {syncError}
                  </div>
                )}

                {/* Contact cards */}
                {contacts.map(c => (
                  <ContactCard key={String(c.id)} contact={c} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* System Info */}
      <Section title="System">
        <dl className="divide-y divide-gray-100">
          <InfoRow label="Client ID" value={client.label} />
          {client.external_id && <InfoRow label="External ID" value={client.external_id} />}
          <InfoRow label="Status" value={client.status} />
          <InfoRow label="Last Updated" value={client.updated_at ? new Date(client.updated_at).toLocaleString() : null} />
          <InfoRow label="Created" value={formatDate(client.created_at)} />
        </dl>
      </Section>
    </div>
  );
};

export default ProfileTab;
